import {
  Heading,
  LiliButton,
  LiliButtonType,
  LiliLinkButton,
  LiliLinkType,
  LiliTextInput,
} from '@lili/lili-frontend-common';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { ControllerWrapper } from '@Hooks/index';
import { SUBMIT_BUTTON_ID } from '@Types/consts.ts';
import { TermsAndConditions } from '@Pages/passwordless/welcome/components/TermsAndConditions.tsx';
import { OnboardingFormFields } from '@Types/index.ts';
import { useWelcomePageLogic } from '@Pages/passwordless/welcome/hooks/useWelcomePageLogic.ts';
import { FirstNameInput } from '@Components/inputs/FirstNameInput.tsx';
import { LastNameInput } from '@Components/inputs/LastNameInput.tsx';
import { PasswordInput } from '@Components/inputs/PasswordInput';
import { EnvironmentVariables, getEnvironmentVariable } from '@Services/env/environment.service';
import { ForgotPasswordModal } from '@Components/modal/ForgotPasswordModal';
import { useResizeListener } from '@Hooks/custom/useResizeListener';
import { useAsyncBlock } from '@Hooks/utils/useAsyncBlock';
import { TrustpilotAndFDICSection } from '../components/valueProposal/DirectValuesProposal';
import { TrustBadges } from '../components/valueProposal/TrustBadges';

export const WelcomeForm = () => {
  const { blockingAction, isBlocked } = useAsyncBlock();
  const {
    handleSubmit,
    formMethods,
    isEmbedded,
    isLoading,
    editMode,
    isButtonDisabled,
    shouldShowExtendedForm,
    onTncClick,
    onPrivacyPolicyClick,
    elementsDisabled,
    isForgotPasswordModalOpen,
    closeForgotPasswordModal,
    handleForgotPasswordRecovery,
    isLoggingIn,
  } = useWelcomePageLogic();
  const { t } = useTranslation();
  const { isMobile } = useResizeListener();
  const handleGoToAccount = () => {
    const redirectUrl = getEnvironmentVariable(EnvironmentVariables.VITE_APP_DEEPLINK);
    window.location.href = redirectUrl;
  };
  return (
    <div className='flex flex-col gap-[12px]'>
      <FormProvider {...formMethods}>
        <ControllerWrapper name={OnboardingFormFields.EMAIL} control={formMethods.control}>
          {({ value, onChange, onBlur, error }) => (
            <LiliTextInput
              tabIndex={0}
              onChange={onChange}
              id={OnboardingFormFields.EMAIL}
              title={t('pages.home.email')}
              onBlur={onBlur}
              value={value}
              disabled={elementsDisabled || isEmbedded}
              errorMessage={error?.message}
              isPublic={false}
            />
          )}
        </ControllerWrapper>
        {shouldShowExtendedForm && <FirstNameInput tabIndex={0} disabled={false} />}
        {shouldShowExtendedForm && <LastNameInput disabled={false} tabIndex={0} />}
        <PasswordInput tabIndex={0} />
        <div
          className={twMerge(
            'flex gap-[24px] flex-col',
            isEmbedded && 'flex-col-reverse items-center gap-[40px]'
          )}>
          <LiliButton
            onClick={() => blockingAction(handleSubmit)}
            isLoading={isLoading || isBlocked}
            id={SUBMIT_BUTTON_ID}
            type={LiliButtonType.PRIMARY}
            title={editMode ? t('pages.home.mainCtaEdit') : t('pages.home.startApplication')}
            disabled={isButtonDisabled || isBlocked}
          />
          {!isEmbedded && (
            <div className='flex items-center justify-center w-full'>
              <Heading.H10 className='text-text-main-tertiary'>
                {t('pages.home.alreadyStarted')}
              </Heading.H10>
              <LiliLinkButton
                id='login-button'
                className='text-[14px] px-[4px] w-fit'
                onClick={handleGoToAccount}
                title={t('pages.home.resume')}
                type={LiliLinkType.QUIET}
              />
            </div>
          )}
          <div className='flex flex-col gap-[24px] justify-center items-center w-full'>
            {isEmbedded && <TrustBadges />}
            <TermsAndConditions
              onTncClick={onTncClick}
              onPrivacyPolicyClick={onPrivacyPolicyClick}
              isEmbedded={isEmbedded}
            />
          </div>
          <p className='text-center text-[12px] leading-[16px] text-text-main-tertiary'>
            {t('pages.home.protectedByRecaptcha')}
          </p>
        </div>
        {isMobile && !isEmbedded && (
          <div className='mb-[20px] mt-[16px]'>
            <TrustpilotAndFDICSection />
          </div>
        )}
      </FormProvider>

      {isForgotPasswordModalOpen && (
        <ForgotPasswordModal
          isOpen={isForgotPasswordModalOpen}
          onPrimaryAction={handleForgotPasswordRecovery}
          onClose={closeForgotPasswordModal}
          isLoading={isLoggingIn}
        />
      )}
    </div>
  );
};
